import { AlertCircleIcon, CheckIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import {
  ToolUserResponse,
  useRespondToDossbotToolConfirmationMutation,
  type ToolRunStep,
} from '~/common/generated/apollo-operations'
import { Message, MessageContent } from '~/components/ai-elements/message'
import { Response } from '~/components/ai-elements/response'

interface ToolConfirmationProps {
  toolRunStep: Pick<ToolRunStep, 'id' | 'confirmationMessage' | 'toolName'>
  chatId: string
}

/**
 * Displays a confirmation dialog for a tool that requires user approval.
 * Shows a user-friendly message and approve/reject buttons.
 */
export function ToolConfirmation({ toolRunStep }: ToolConfirmationProps) {
  const [respondToDossbotToolConfirmation, { loading }] =
    useRespondToDossbotToolConfirmationMutation()
  const [localResponse, setLocalResponse] = useState<ToolUserResponse | null>(null)
  const [userRejectionReason, setUserRejectionReason] = useState<string | undefined>(undefined)
  const [showRejectionReason, setShowRejectionReason] = useState(false)

  const handleResponse = async (response: ToolUserResponse) => {
    setLocalResponse(response)
    try {
      await respondToDossbotToolConfirmation({
        variables: {
          input: {
            toolRunStepId: toolRunStep.id,
            response,
            // Only include rejection reason if user is rejecting
            ...(response === ToolUserResponse.REJECTED && {
              // Default rejection reason if user doesn't provide one so that dossbot doesn't say system rejected it
              userRejectionReason: userRejectionReason || 'User has rejected this tool execution',
            }),
          },
        },
      })
    } catch (error) {
      console.error('Failed to respond to tool confirmation:', error)
      setLocalResponse(null)
    }
  }

  const handleRejectClick = () => {
    setShowRejectionReason(true)
  }

  const handleRejectionReasonKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleResponse(ToolUserResponse.REJECTED)
    }
  }

  // Show pending state if user has responded but waiting for backend
  if (localResponse) {
    const icon =
      localResponse === ToolUserResponse.APPROVED ? (
        <CheckIcon className="text-green-600" size={20} />
      ) : (
        <XIcon className="text-red-600" size={20} />
      )
    const text = localResponse === ToolUserResponse.APPROVED ? 'Approved' : 'Rejected'

    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-4 bg-gray-2 p-4">
        {icon}
        <span className="text-sm text-gray-11">{text}</span>
      </div>
    )
  }

  const confirmationText = toolRunStep.confirmationMessage || 'This action requires your approval.'

  return (
    <div className="rounded-lg border border-gray-6 p-4">
      <div className="mb-2 flex items-center gap-2">
        <AlertCircleIcon className="text-gray-11" size={20} />
        <span className="text-sm font-medium text-gray-11">Confirmation Required</span>
      </div>

      <div className="mb-4">
        <Message className="py-0 ps-0" from="assistant">
          <MessageContent className="[&_*]:!text-[14px]" variant="flat">
            <Response>{confirmationText}</Response>
          </MessageContent>
        </Message>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <Button
            className="bg-green-9 hover:bg-green-10"
            disabled={loading}
            onClick={() => handleResponse(ToolUserResponse.APPROVED)}
            size="sm"
            startIcon={<CheckIcon className="text-white" size={20} />}
            variant="primary"
          >
            {loading ? 'Approving...' : 'Approve'}
          </Button>
          <Button
            className="!h-[38px]"
            disabled={loading || showRejectionReason}
            onClick={handleRejectClick}
            size="sm"
            startIcon={<XIcon className="text-white" size={20} />}
            variant="destructive"
          >
            {loading ? 'Rejecting...' : 'Reject'}
          </Button>
        </div>
        {showRejectionReason && (
          <ShortTextInput
            autoFocus
            className="w-full bg-white"
            label="Rejection Reason"
            onChange={(e) => setUserRejectionReason(e.target.value)}
            onKeyDown={handleRejectionReasonKeyDown}
            placeholder="Enter reason for rejection"
            value={userRejectionReason}
            wrapperClassName="p-0"
          />
        )}
      </div>
    </div>
  )
}
